import { ActionIcon, Group, Menu, Text } from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconPlus } from '@tabler/icons-react';
import { useState } from 'react';
import { Button } from 'src/design-system/Button';
import { NameFormat, formatName } from 'src/utils/nameFormatting';
import { GroupMember } from '../grouping';

type MemberPickerProps = {
  members: GroupMember[];
  selectedClientIds: string[];
  onSelect: (clientId: string) => void;
};

/**
 * Members already carrying an amount stay listed but inert, so the adviser can see the whole
 * family at once rather than losing track of who is already in.
 */
export function MemberPicker({ members, selectedClientIds, onSelect }: MemberPickerProps) {
  const [opened, setOpened] = useState(false);

  return (
    <Menu
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      withinPortal={false}
      closeOnItemClick={false}
    >
      <Menu.Target>
        <Button
          variant="text"
          data-testid="add-family-member"
          h={24}
          px={8}
          style={{ flexShrink: 0 }}
          data-expanded={opened || undefined}
        >
          <Group
            gap={4}
            wrap="nowrap"
            justify="center"
            align="center"
            style={{ whiteSpace: 'nowrap' }}
          >
            Add family member
            {opened ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
          </Group>
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        {members.map(member => {
          const isSelected = selectedClientIds.includes(member.clientId);
          const name = formatName(member.firstName, member.lastName, NameFormat.NATURAL);

          return (
            <Menu.Item
              key={member.clientId}
              disabled={isSelected}
              onClick={() => onSelect(member.clientId)}
              data-testid={`member-option-${member.clientId}`}
            >
              <Group justify="space-between" wrap="nowrap" gap="xl">
                <Text c={isSelected ? 'gray.4' : 'gray.10'}>{name}</Text>
                {isSelected ? (
                  <Text size="sm" c="gray.4">
                    Added
                  </Text>
                ) : (
                  <ActionIcon variant="transparent" c="gray.4" component="div" aria-hidden>
                    <IconPlus size={16} />
                  </ActionIcon>
                )}
              </Group>
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
}
